/**
 * GateBreachControl — "At what angle is it a failure?"
 *
 * The coach-facing half of PR-5's coach-typed failure line (REFACTOR_PLAN.md
 * §C.3c). Shown only on a check that is a HARD LIMIT: the rotation shape
 * (`use_rotation_angle` + `is_gate` + `rotation_profile`) is the only shape
 * that has a failure angle at all today, so it is the only shape that gets
 * this field.
 *
 * It shows three things the studio never showed before:
 *   1. the question, as a number field — the angle past which an attempt fails,
 *   2. what typing a number does, in one sentence,
 *   3. which number is in force right now, from the backend's own verdict.
 *
 * NOT THE SAME NUMBER AS `StrictnessControl`. That one answers "how strict"
 * (how far off still scores well); this one answers "when is it a failure".
 * They sit on the same check and are deliberately never combined into one
 * field or one sentence — treating the strictness as the failure line is
 * exactly what made a coach's "fail above 20°" into a failure at 60°
 * (§C.3c's unit rule). This component writes ONLY `coach_breach_degrees`
 * and reads nothing from the strictness.
 *
 * Empty is a real answer. A hard limit with no failure angle grades rather
 * than fails, the source line says so, and nothing here supplies a default —
 * §C.3c: "Never a placeholder threshold."
 *
 * Copy lives in `@/utils/gateCopy`; this component is layout only.
 */
import { useId } from 'react';
import { cn } from '@/utils/cn';
import {
  BREACH_ANGLE_EXPLANATION,
  BREACH_ANGLE_FIELD_LABEL,
  BREACH_ANGLE_HOW,
  gateThresholdSourceLine,
  unsavedGateThresholdSource,
} from '@/utils/gateCopy';
import type { GateSummary } from '@/types/api';

export interface GateBreachControlProps {
  /** The failure angle the coach has typed, or null when none is set. */
  breachDegrees: number | null;
  /**
   * The backend's verdict for this check, when it has one. Its
   * `threshold_source` is AUTHORITATIVE for the "which number is in force"
   * line: the studio never works that out from the config, because a second
   * answer to the same question is the bug class this refactor removes. Omit
   * on a check that has never been saved — the line then describes the
   * unsaved state, which is the only fact available.
   */
  gateSummary?: GateSummary | null;
  /** Called with the new failure angle, or null when the field is cleared. */
  onChange: (breachDegrees: number | null) => void;
  /** Narrowest / widest the field accepts. Caller-supplied, never invented. */
  min?: number;
  max?: number;
  className?: string;
}

/**
 * Bounds on the typed failure angle. A torso-plane deviation is measured from
 * profile and cannot exceed a quarter turn, so 90 is the widest meaningful
 * answer and 1 the narrowest a coach could act on. These are FIELD bounds, not
 * a scoring constant: they stop the coach typing a number the save would
 * reject, and neither is used as a default.
 */
const FIELD_MIN_DEGREES = 1;
const FIELD_MAX_DEGREES = 90;

export default function GateBreachControl({
  breachDegrees,
  gateSummary,
  onChange,
  min = FIELD_MIN_DEGREES,
  max = FIELD_MAX_DEGREES,
  className,
}: GateBreachControlProps) {
  const fieldId = useId();

  // The saved verdict wins whenever there is one. With none, the check has
  // never reached the backend, so the only truthful thing to say is whether
  // the coach has typed a number yet.
  const effectiveSource =
    gateSummary?.threshold_source ?? unsavedGateThresholdSource(breachDegrees);

  const handleChange = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      onChange(null);
      return;
    }
    const parsed = Number.parseFloat(trimmed);
    if (Number.isNaN(parsed)) return;
    onChange(Math.min(Math.max(parsed, min), max));
  };

  return (
    <div
      className={cn('space-y-2', className)}
      data-testid="gate-breach-control"
      data-gate-threshold-source={effectiveSource}
    >
      <label
        htmlFor={fieldId}
        className="block text-xs font-semibold text-text-primary"
      >
        {BREACH_ANGLE_FIELD_LABEL}
      </label>

      {/* Stacks at phone width so the field never pushes the unit off the edge. */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={fieldId}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={1}
          value={breachDegrees ?? ''}
          onChange={(e) => handleChange(e.target.value)}
          className={cn(
            'w-20 rounded-lg border border-border-subtle bg-dark-elevated-1 px-2.5 py-1.5',
            'text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-amber-500/40',
          )}
          data-testid="gate-breach-degrees-input"
        />
        <span className="text-[11px] text-text-secondary">degrees</span>
      </div>

      <p className="text-[11px] leading-relaxed text-text-secondary">
        {BREACH_ANGLE_EXPLANATION}
      </p>

      <p
        className={cn(
          'text-[11px] font-medium leading-relaxed',
          effectiveSource === 'none' ? 'text-warning' : 'text-success',
        )}
        data-testid="gate-threshold-source-line"
      >
        {gateThresholdSourceLine(effectiveSource)}
      </p>

      <p className="text-[11px] leading-relaxed text-text-muted">
        {BREACH_ANGLE_HOW}
      </p>
    </div>
  );
}
